import { PluginJsonSchema, PluginJsonSchemaProperty } from './plugin-schema-types'

type CoercionSuccess = { success: true; data: Record<string, unknown> }
type CoercionFailure = { success: false; errors: Record<string, string> }
export type CoercionResult = CoercionSuccess | CoercionFailure

function coerceBoolean(value: string): boolean | undefined {
  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }
  return undefined
}

function coerceArrayElements(
  elements: string[],
  itemsSchema: PluginJsonSchemaProperty | undefined,
): { values: unknown[] } | { error: string } {
  const itemType = itemsSchema?.type
  const nonEmpty = elements.filter((el) => el !== '')

  if (itemType === 'number' || itemType === 'integer') {
    const numbers = nonEmpty.map(Number)
    if (numbers.some(isNaN)) {
      return { error: 'All values must be numbers' }
    }
    return { values: numbers }
  }

  if (itemType === 'boolean') {
    const booleans = nonEmpty.map(coerceBoolean)
    if (booleans.some((b) => b === undefined)) {
      return { error: 'All values must be true or false' }
    }
    return { values: booleans as boolean[] }
  }

  // string or unknown item type: pass through as-is
  return { values: nonEmpty }
}

/**
 * Takes the string values from text fields and the JSON Schema and returns either
 * coerced typed values or a map of field errors.
 */
export function coerceConfigValues(
  values: Record<string, string>,
  schema: PluginJsonSchema,
): CoercionResult {
  const errors: Record<string, string> = {}
  const data: Record<string, unknown> = {}
  const requiredFields = schema.required || []

  if (!schema.properties) {
    return { success: true, data }
  }

  for (const [key, propertyDef] of Object.entries(schema.properties)) {
    const value = values[key] || ''
    const isRequired = requiredFields.includes(key)
    const propertySchema = typeof propertyDef === 'boolean' ? undefined : propertyDef

    if (value === '') {
      if (isRequired) {
        errors[key] = 'This field is required'
      }
      // Non-required empty fields: omit from output
      continue
    }

    const type = propertySchema?.type

    if (type === 'number' || type === 'integer') {
      const parsed = Number(value)
      if (isNaN(parsed)) {
        errors[key] = 'Must be a number'
      } else {
        data[key] = parsed
      }
    } else if (type === 'boolean') {
      const parsed = coerceBoolean(value)
      if (parsed === undefined) {
        errors[key] = 'Must be true or false'
      } else {
        data[key] = parsed
      }
    } else if (type === 'array') {
      const elements = value.split(',').map((el) => el.trim())

      // Empty array from splitting an empty-ish string: omit if non-required
      if (elements.length === 1 && elements[0] === '') {
        if (isRequired) {
          errors[key] = 'This field is required'
        }
        continue
      }

      const result = coerceArrayElements(elements, propertySchema?.items)
      if ('error' in result) {
        errors[key] = result.error
      } else {
        data[key] = result.values
      }
    } else {
      // string, unknown type, or no type: passthrough
      data[key] = value
    }
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors }
  }

  return { success: true, data }
}

/**
 * Formats a real typed value (from backend) into a display string for a text field.
 */
export function formatValueForDisplay(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }

  if (Array.isArray(value)) {
    return value.join(', ')
  }

  return String(value)
}
